import React, { useCallback } from 'react'
import * as R from 'ramda'

import FilterGroup from 'shared/components/FilterGroup'
import FilterInput from 'components/FilterInput'
import { FilterGroupProvider } from 'shared/components/FilterGroup/context'
import useCustomAttributeOptions from 'hooks/useCustomAttributeOptions'

import type { DropdownItemProps } from 'semantic-ui-react'
import type { OnChangeValue } from 'shared/components/FilterGroup'
import type { WidgetSettingsState } from 'components/Admin/FeedbackWidgetSettings'
import type { Option, Extra } from 'organisms/FeedbackFilterGroup/container'
import CustomFilterValueInputForIsComparison from 'components/CustomFilterValueInputForIsComparison'

export default function TestimonialWidgetFilter({
  widgetSettingsState,
  setWidgetState,
  surveyOptions,
}: TestimonialWidgetFilterProps) {
  const handleChange = useCallback(
    function handleAnswerFilterChange(filter: OnChangeValue<any>) {
      setWidgetState((prevData) => ({
        ...prevData,
        answerFilter: convertFilterToRequest(filter),
      }))
    },
    [setWidgetState]
  )

  const customAttributeOptions = useCustomAttributeOptions()
  const isCustomAttributeOptionsLoading = customAttributeOptions == null
  const isLoading =
    !widgetSettingsState?.answerFilter || !surveyOptions || isCustomAttributeOptionsLoading

  if (isLoading) {
    return null
  }

  const options = baseFilterOptions.concat(customAttributeOptions)
  const defaultValue = convertAnswerFilterResponseToRules(
    widgetSettingsState?.answerFilter,
    options,
    surveyOptions
  )

  return (
    <FilterGroupProvider>
      <FilterGroup
        condition="and"
        onChange={handleChange}
        options={options}
        defaultValue={defaultValue}
        comparisons={[
          { label: 'is', value: 'is', inputComponent: CustomFilterValueInputForIsComparison },
        ]}
      />
    </FilterGroupProvider>
  )
}

function convertAnswerFilterResponseToRules(
  answerFilter: { and: AnswerFilterRule[] },
  options: FilterOption[],
  surveyOptions: SurveyOption[]
): FilterOption[] {
  return answerFilter?.and.map((rule) => {
    const { key, filtered_values } = rule
    const filters = filtered_values.map((filtered_value) => {
      if (key === 'survey') {
        const surveyName = getSurveyName(filtered_value, surveyOptions)
        return { value: filtered_value, label: surveyName }
      }
      return { value: filtered_value, label: filtered_value }
    })

    const selectedOption = findSelectedOption(rule, options)
    return {
      ...(selectedOption as object),
      filters: filters,
    } as FilterOption
  })
}

function getSurveyName(surveyToken: string, surveyOptions: SurveyOption[]): string {
  const surveyOption = R.find(R.propEq('value', surveyToken), surveyOptions) as SurveyOption
  const surveyName = R.prop('text', surveyOption) || surveyToken
  return surveyName
}

function findSelectedOption(rule: AnswerFilterRule, options: FilterOption[]): FilterOption {
  const value = `${rule.key}${rule?.attribute || ''}`
  return R.find((option) => {
    const optionValue = `${option.field}${option?.extra?.attribute || ''}`
    return value === optionValue
  }, options) as FilterOption
}

const baseFilterOptions: Option<Extra>[] = [
  {
    field: 'published_as_testimonial',
    label: 'Published',
    icon: 'far fa-bullhorn',
    comparison: 'is',
    filters: [{ value: true }],
  },
  {
    field: 'survey',
    label: 'Survey',
    icon: 'filter',
    comparison: 'is',
  },
  {
    field: 'choice_value',
    label: 'Rating value',
    icon: 'smile',
    comparison: 'is',
  },
  {
    field: 'sentiment',
    label: 'Sentiment',
    icon: 'smile',
    comparison: 'is',
  },
  {
    field: 'metric',
    label: 'Metric',
    icon: 'chart line',
    comparison: 'is',
  },
  {
    field: 'ticket_id',
    label: 'Ticket ID',
    icon: 'far fa-ticket',
    comparison: 'is',
  },
  {
    field: 'language',
    label: 'Language',
    icon: 'far fa-globe',
    comparison: 'is',
  },
  {
    field: 'team_member',
    label: 'Team member',
    icon: 'far fa-user-headset',
    comparison: 'is',
  },
  {
    field: 'collaborator',
    label: 'Collaborator',
    icon: 'far fa-user-headset',
    comparison: 'is',
  },
  {
    field: 'customer',
    label: 'Customer',
    icon: 'far fa-user',
    comparison: 'is',
  },
  {
    field: 'company',
    label: 'Company',
    icon: 'building',
    comparison: 'is',
  },
  {
    field: 'ip',
    label: 'IP address',
    icon: 'street view',
    inputComponent: FilterInput,
    comparison: 'is',
  },
  {
    field: 'tag',
    label: 'Tag',
    icon: 'tag',
    comparison: 'is',
  },
  {
    field: 'customer.tag',
    label: 'Customer Tag',
    icon: 'tag',
    comparison: 'is',
  },
  {
    field: 'source',
    label: 'Source',
    icon: 'filter',
    comparison: 'is',
  },
]

function convertFilterToRequest(filter: OnChangeValue<any>) {
  if (filter) {
    return {
      [filter.condition]: filter.rules.map((rule) => {
        return {
          key: rule.field,
          filtered_values: rule.filters.map((filter) => filter.value),
          attribute: rule.extra?.attribute,
          comparison: rule.comparison,
        }
      }),
    }
  }
}

type FilterOption = {
  field: string
  extra: { attribute: string }
  label: string
  icon: string
  inputComponent: React.Component
  comparison: string
  filters: { value: string; label: string }[]
}
type SurveyOption = {
  text: string
  value: string
}

type AnswerFilterRule = {
  key: string
  filtered_values: string[]
  attribute?: string
}
export type AnswerFilter = { and: AnswerFilterRule[] }

type TestimonialWidgetFilterProps = {
  widgetSettingsState: WidgetSettingsState
  setWidgetState: Function
  surveyOptions?: DropdownItemProps[]
}



// WEBPACK FOOTER //
// ./src/organisms/TestimonialWidgetFilter/index.tsx